/**
 * Where a rules document came from, and what the importer had to guess.
 *
 * `machina import` drafts a rules document from an SRD. A draft is not a
 * system: the model read prose written for humans and produced a machine
 * format, and everywhere the prose was silent it had to either invent
 * something or say so. This block is where it says so.
 *
 * THE POINT OF THE BLOCK IS THAT VALIDATION FAILS WHILE IT HAS ENTRIES.
 * An AI GM running a rules document does not know the difference between a
 * rule the author wrote and a rule the importer inferred — it will run both
 * with the same confidence, in the same voice, to a table that also cannot
 * tell. So the unanswered questions are not a warning. They are the reason
 * `machina validate` says no, and the author clears them one at a time by
 * checking the source and deleting the entry.
 *
 * It is also the attribution record. Every licence Machina will import under
 * requires it, and "which document, which revision, imported when" is the
 * thing nobody can reconstruct later.
 */
import { z } from 'zod';
/** How sure the importer was about a value it wrote down. */
export const ImportConfidenceSchema = z.enum([
    /** Stated outright in the source text. Recorded for provenance, not doubt. */
    'stated',
    /** Implied by the source but never said. The author should confirm. */
    'inferred',
    /** The source is silent and the runtime needed a value. A placeholder. */
    'invented',
]);
/**
 * One thing the importer could not settle on its own.
 *
 * `path` points at the field so a reviewer can go straight to it, and the
 * document still carries the importer's best guess in that field — an empty
 * field would make the document unreadable, and the whole review would become
 * "write the system from scratch, but in YAML."
 */
export const ImportQuestionSchema = z.object({
    /** Dotted path into the rules document: `coreResolution.poolReading`. */
    path: z.string(),
    /** Asked of a human, in one sentence, answerable by reading the source. */
    question: z.string(),
    /** Why the importer landed where it did. */
    reasoning: z.string().optional(),
    confidence: ImportConfidenceSchema,
});
export const ImportProvenanceSchema = z.object({
    /** The document this was drafted from — filename, title, or URL. */
    source: z.string(),
    /** ISO 8601. */
    importedAt: z.string().optional(),
    /** Which model drafted it, so a bad batch can be found again. */
    model: z.string().optional(),
    /**
     * Everything still unanswered. Non-empty means the document does not
     * validate; see `validateGameSystemModule`.
     */
    unresolved: z.array(ImportQuestionSchema).default([]),
});
/** The questions a reviewer still owes an answer to. */
export function unresolvedQuestions(provenance) {
    return provenance?.unresolved ?? [];
}
/** One line per question, ready to print. */
export function formatUnresolved(question) {
    const suffix = question.reasoning ? ` — ${question.reasoning}` : '';
    return `${question.path} [${question.confidence}]: ${question.question}${suffix}`;
}
//# sourceMappingURL=provenance.js.map